import { HttpClient } from '@angular/common/http';
import { Inject, inject, Injectable, signal } from '@angular/core';

import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';

import { AIPromptCore, IFollowUp, InputType, QuestionType, SourceVariables, SurveyMetadata, SurveyResult } from '@empathy/survey/base';
import { Question, SurveyModel } from '@empathy/survey-core';

import { SurveyConditionalService } from './survey-conditional.service';
import { EnvironmentConfig } from '../config/environment.config';
import { PageControls, QuestionControl } from '../models/page-control.interface';

@Injectable({ providedIn: 'root' })
export class SurveyViewerService {
  inviteId$ = signal(null);
  isTestMode$ = signal(false);
  activePageIndex$ = signal(null);
  isBackButtonDisabled$ = signal(false);
  forceExit$ = new Subject<string>();

  aiFollowupCall: (prompt: AIPromptCore) => Observable<IFollowUp>;
  pageControls: PageControls = {};
  questionControls: QuestionControl = {};
  questions: { [questionId: string]: Question } = {};

  private conditionalService = inject(SurveyConditionalService);
  private http = inject(HttpClient);

  constructor(@Inject('environment') private environment: EnvironmentConfig) {}

  initForm(survey: SurveyModel, variables: SourceVariables): void {
    this.isBackButtonDisabled$.set(survey.settings?.isBackButtonDisabled ?? false);
    this.aiFollowupCall = survey.aiFollowupCall;

    survey.pages.forEach((page) => {
      this.pageControls[page.id] = {};

      page.questions.forEach((question) => {
        this.mapQuestionControl(page.id, question);
        this.conditionalService.mapConditionality(question, this.questions, this.questionControls);
      });
    });

    survey.pages = survey.pages.filter((page) => page.questions.length > 0);

    // this.processSourceVariables(survey, variables);
  }

  resumeSurvey(answers: SurveyResult): void {
    for (const qId in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, qId)) {
        const qControl = this.questionControls[qId];
        if (qControl) {
          const question = this.questions[qId];
          if (question) {
            const value = JSON.parse(answers[qId] as string);
            qControl.patchValue(value);
            this.conditionalService.run(qId);
          }
        }
      }
    }

    const noEmptyQuestions = Object.values(this.questions).filter(
      (q) => !!q.pageId && q.visible && !q.isEmpty(JSON.stringify(this.questionControls[q.id].value))
    );
    if (noEmptyQuestions.length === 0) {
      this.activePageIndex$.set(-1);
      return;
    }

    const lastNoEmptyQ = noEmptyQuestions[noEmptyQuestions.length - 1];
    this.activePageIndex$.set(Object.keys(this.pageControls).findIndex((pageId) => lastNoEmptyQ.id in this.pageControls[pageId]));
  }

  mapMetadata(pageId: string, terminatedQuestionId?: string): SurveyMetadata[] {
    const pageControl = this.pageControls[pageId];
    const surveyMetadata: SurveyMetadata[] = [];

    // for (const qId in pageControl) {
    //   if (Object.prototype.hasOwnProperty.call(pageControl, qId)) {
    //     const qControl = pageControl[qId];
    //     const question = this.questions[qId];

    //     if (question?.visible && qControl.value) {
    //       const metadata = new SurveyMetadata(qId, qId === terminatedQuestionId);

    //       if (CarouselType.includes(question.type)) {
    //         metadata.selectedCarouselValues = this.mapCardOptionIndexSelected(question, qControl.value);
    //       } else if (ChoiceQuestionTypes.includes(question.type) || question.type === QuestionType.Rating || question.type === QuestionType.RankSort) {
    //         metadata.selectedValues = this.mapOptionIndexSelected(question.options, (qControl.value as ChoiceTypeResult).value);
    //       } else if (question.type === QuestionType.FileUpload) {
    //         metadata.selectedValues = [{ id: 'MEDIA_ID_LIST', title: (qControl.value as Media[]).map((m) => m.mediaId).join(', ') } as SelectedValueIndex];
    //       } else if (question.type !== QuestionType.AIChat && question.type !== QuestionType.InfoPage) {
    //         metadata.selectedValues = [{ id: 'PLAIN_VALUE', title: qControl.value } as SelectedValueIndex];
    //       }

    //       surveyMetadata.push(metadata);
    //     }
    //   }
    // }

    return surveyMetadata;
  }

  mapAnswers(): SurveyResult {
    return Object.keys(this.questionControls).reduce((results, qId) => {
      results[qId] = JSON.stringify(this.questionControls[qId].getRawValue());
      return results;
    }, {} as SurveyResult);
  }

  destroy(): void {
    this.pageControls = {};
    this.questionControls = {};
    this.questions = {};
    this.conditionalService.destroy();
  }

  private mapQuestionControl(pageId: string, question: Question): void {
    const qControl = question.getFormControl();

    this.pageControls[pageId][question.id] = qControl;
    this.questionControls[question.id] = qControl;
    this.questions[question.id] = question;

    if (question.monitorTerms?.monitorProblematicInd) {
      this.mapQuestionAsyncValidators(question, qControl);
    }

    qControl.valueChanges.subscribe(() => {
      this.conditionalService.run(question.id);
    });
  }

  private mapQuestionAsyncValidators(question: Question, qControl: FormControl<any> | FormArray<any> | FormGroup<any>): void {
    switch (question.type) {
      case QuestionType.Input: {
        if (question.inputType === InputType.Text) {
          qControl.addAsyncValidators(this.validateBlockedTerms(question.id));
        }
        break;
      }
      case QuestionType.Essay: {
        qControl.addAsyncValidators(this.validateBlockedTerms(question.id));
        break;
      }

      default:
        return;
    }
  }

  private validateBlockedTerms(id: string): AsyncValidatorFn {
    const minCharactersNeeded = 3;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      if (control.value.length < minCharactersNeeded) {
        return of({
          message: `Input must be at least ${minCharactersNeeded} characters long.`,
          isCustom: true
        });
      }

      return control.valueChanges.pipe(
        startWith(control.value),
        debounceTime(2000),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (!value || value.length < minCharactersNeeded) {
            return of(null);
          }

          return this.isBlocked(id, this.mapAnswers()).pipe(
            map((response) =>
              response.isBlocked
                ? { unique: true, message: response.errorMessage || 'Please review your input and try again before continuing.', isCustom: true }
                : null
            ),
            catchError(() => of(null))
          );
        }),
        take(1)
      );
    };
  }

  private isBlocked(questionId: string, answers: SurveyResult): Observable<{ isBlocked: boolean; errorMessage: string }> {
    return this.http
      .post<{
        isBlocked: boolean;
        errorMessage: string;
      }>(`${this.environment.surveyEndPoint}${this.inviteId$()}/block-terms`, { questionId, answers })
      .pipe(catchError(() => of(null)));
  }

  // private processSourceVariables(survey: SurveyModel, sourceVariables: SourceVariables): void {
  //   // EQ-446 For the moment we will consider that variables will either have a value or are
  //   // not needed so won't get included into the answer object and neither shown as normal question.

  //   const sourceVariableTokens = Object.keys(sourceVariables || {});
  //   const configuredVariables = survey.sources ? survey.sources.flatMap((source) => source.variables) : [];

  //   if (sourceVariableTokens.length === 0 || configuredVariables.length === 0) {
  //     return;
  //   }

  //   const variableQuestions = [];
  //   sourceVariableTokens.forEach((token) => {
  //     const sourceVariable = configuredVariables.find((variable) => variable.variableToken === token);

  //     if (sourceVariable) {
  //       const qControl = sourceVariable.getFormControl();
  //       if (sourceVariable.type === QuestionType.SingleChoice) {
  //         qControl.setValue({ value: sourceVariables[token], otherText: '' });
  //       } else {
  //         qControl.setValue(sourceVariables[token]);
  //       }

  //       variableQuestions.push(sourceVariable);
  //       this.questions[sourceVariable.id] = sourceVariable;
  //       this.questionControls[sourceVariable.id] = qControl;

  //       this.conditionalService.mapConditionality(sourceVariable, this.questions, this.questionControls);
  //     }
  //   });

  //   variableQuestions.forEach((question) => this.conditionalService.run(question.id));
  // }
}
